import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, CheckCircle2, ExternalLink } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { IMAGES, CONTACT, VEHICLE_TYPES, SERVICE_TYPES } from "../mock";
import { api } from "../lib/api";

const emptyForm = { name: "", email: "", phone: "", vehicleType: "", vehicleModel: "", serviceType: "", date: "", message: "" };

const Booking = () => {
  const { t } = useLanguage();
  const { content } = useContent();
  const contact = {
    address: content.contact_address || CONTACT.address,
    addressFull: content.contact_address_full || CONTACT.addressFull,
    phone: content.contact_phone || CONTACT.phone,
    phoneRaw: (content.contact_phone || CONTACT.phone).replace(/[^0-9+]/g, ""),
    email: content.contact_email || CONTACT.email,
    hours: content.contact_hours || CONTACT.hours,
  };
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createBooking(form);
      setSubmitted(true);
      setForm(emptyForm);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      alert("Erro ao enviar o pedido. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bf-input w-full rounded-xl px-4 py-3 text-sm placeholder:text-gray-500";
  const labelCls = "block text-sm font-medium text-gray-300 mb-2";

  const contactItems = [
    { icon: MapPin, title: contact.address, sub: contact.addressFull },
    { icon: Phone, title: t("contact_phone"), sub: contact.phone, href: `tel:${contact.phoneRaw}` },
    { icon: Mail, title: t("contact_email"), sub: contact.email, href: `mailto:${contact.email}` },
    { icon: Clock, title: t("contact_hours"), sub: contact.hours },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.bookingHero} alt="Booking hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070b18]/85 via-[#070b18]/85 to-[#070b18]" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto bf-section-fade">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">{t("bp_title")}</h1>
          <p className="text-lg md:text-xl text-gray-300">{t("bp_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#0a1024]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-10">
          {/* Form */}
          <div className="lg:col-span-2 bf-glass rounded-2xl p-8">
            {submitted && (
              <div className="mb-6 flex items-center gap-3 bg-[#22d3ee]/10 border border-[#22d3ee]/30 text-[#67e8f9] rounded-xl px-4 py-3 text-sm">
                <CheckCircle2 className="w-5 h-5" /> {t("f_success")}
              </div>
            )}
            <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t("f_name")} *</label>
                <input name="name" required value={form.name} onChange={onChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t("f_email")} *</label>
                <input type="email" name="email" required value={form.email} onChange={onChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t("f_phone")} *</label>
                <input name="phone" required value={form.phone} onChange={onChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t("f_vehicle_type")} *</label>
                <select name="vehicleType" required value={form.vehicleType} onChange={onChange} className={inputCls}>
                  <option value="">{t("f_select")}</option>
                  {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("f_vehicle_model")} *</label>
                <input name="vehicleModel" required value={form.vehicleModel} onChange={onChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t("f_service_type")} *</label>
                <select name="serviceType" required value={form.serviceType} onChange={onChange} className={inputCls}>
                  <option value="">{t("f_select")}</option>
                  {SERVICE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>{t("f_date")} *</label>
                <input type="date" name="date" required value={form.date} onChange={onChange} className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>{t("f_message")}</label>
                <textarea name="message" rows={4} value={form.message} onChange={onChange} className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} className="bf-btn-primary text-white font-semibold px-8 py-4 rounded-xl w-full uppercase tracking-wide text-sm disabled:opacity-60">
                  {loading ? "..." : t("f_submit")}
                </button>
              </div>
            </form>
          </div>

          {/* Contact sidebar */}
          <div className="space-y-6">
            <div className="bf-glass rounded-2xl p-6">
              <h3 className="text-white font-bold text-lg mb-5">{t("contact_title")}</h3>
              <ul className="space-y-5">
                {contactItems.map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#22d3ee]/10 border border-[#22d3ee]/20 flex items-center justify-center shrink-0">
                      <c.icon className="w-4 h-4 text-[#22d3ee]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{c.title}</p>
                      {c.href ? (
                        <a href={c.href} className="text-gray-400 text-sm hover:text-[#67e8f9] transition-colors">{c.sub}</a>
                      ) : (
                        <p className="text-gray-400 text-sm">{c.sub}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <a
                href={`https://www.google.com/maps/search/${encodeURIComponent(contact.addressFull)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 bf-btn-ghost rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
              >
                {t("open_maps")} <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="bf-glass rounded-2xl p-6">
              <h3 className="text-white font-bold text-lg mb-5">{t("expect_title")}</h3>
              <ul className="space-y-3">
                {[t("expect_1"), t("expect_2"), t("expect_3"), t("expect_4")].map((e, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                    <CheckCircle2 className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /> {e}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
